import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../store";
import {
  fetchAllTopics,
  selectAllTopics,
  selectTopicsLoading,
} from "../store/topics/slice";
import { useTranslation } from "react-i18next";
import SearchInput from "../components/common/SearchInput";
import PageHeader from "../components/common/PageHeader";
import TopicsList from "../components/topics/TopicsList";
import SEO from "../components/common/SEO";

const Topics: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const topics = useSelector(selectAllTopics);
  const isLoading = useSelector(selectTopicsLoading);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    dispatch(fetchAllTopics());
  }, [dispatch]);

  // SEO metadata based on topics state
  const getSEOData = () => {
    if (searchQuery) {
      return {
        title: `${t('topics.title')} - ${searchQuery}`,
        description: t('topics.description'),
        keywords: [
          ...topics.filter(topic => 
            topic.title.toLowerCase().includes(searchQuery.toLowerCase())
          ).map(topic => topic.title),
          'Jewish topics',
          'Torah topics',
          'Jewish learning',
          'Torah study'
        ]
      };
    }

    return {
      title: t('navigation.topics'),
      description: t('topics.description'),
      keywords: [
        ...topics.slice(0, 10).map(topic => topic.title), // Include first 10 topics as keywords
        'Jewish topics',
        'Torah topics',
        'Jewish learning',
        'Torah study'
      ]
    };
  };

  const seoData = getSEOData();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-4rem)]">
        <SEO title={t('topics.title')} description={t('topics.description')} />
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background-primary">
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
      />
      <PageHeader
        title={t("topics.title")}
        description={t("topics.description")}
      >
        <div className="max-w-2xl mx-auto">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t("topics.searchPlaceholder")}
            size="lg"
            className="w-full"
          />
        </div>
      </PageHeader>

      <main className="flex-1 container mx-auto lg:max-w-7xl px-0 md:px-4 lg:px-8 py-12">
        <TopicsList topics={topics} searchQuery={searchQuery} />
      </main>
    </div>
  );
};

export default Topics;
